/**
 * photoil-revision-option.js
 *
 * Adds the paid "作品完成前確認・1回修正オプション" add-on to the photoil
 * product page, and keeps the corresponding cart lines in sync.
 *
 * This file intentionally does NOT modify assets/product-form.js,
 * assets/cart.js, assets/cart-drawer.js or assets/cart-notification.js.
 * When the add-on checkbox is unchecked, none of the code below runs and
 * the existing add-to-cart / cart update behaviour is completely
 * untouched. See the accompanying work report for the reasoning.
 */

(function () {
  'use strict';

  function generateReference() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return 'photoil-' + window.crypto.randomUUID();
    }
    return 'photoil-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  /* ---------------------------------------------------------------------
   * Product page card: checkbox, hidden properties, dynamic checkout guard
   * ------------------------------------------------------------------- */

  if (!customElements.get('photoil-revision-option-card')) {
    customElements.define(
      'photoil-revision-option-card',
      class PhotoilRevisionOptionCard extends HTMLElement {
        connectedCallback() {
          // connectedCallback (rather than the constructor) so this reliably
          // re-runs whenever this element is (re)inserted into the DOM - e.g.
          // when the theme editor replaces the section's markup - instead of
          // relying on the constructor having run before its children exist.
          if (this.photoilBound) return;
          this.photoilBound = true;

          this.checkbox = this.querySelector('[data-photoil-revision-checkbox]');
          this.propInputs = Array.from(this.querySelectorAll('[data-photoil-prop]'));
          this.referenceInput = this.querySelector('[data-photoil-prop="reference"]');
          this.errorEl = this.querySelector('.photoil-revision-option__error');

          if (this.checkbox) this.checkbox.disabled = false;

          this.onCheckboxChange = this.onCheckboxChange.bind(this);
          if (this.checkbox) this.checkbox.addEventListener('change', this.onCheckboxChange);

          this.syncDynamicCheckout();
          this.syncPropInputs();
        }

        get form() {
          const id = this.dataset.productFormId;
          return id ? document.getElementById(id) : null;
        }

        get productForm() {
          const form = this.form;
          return form ? form.closest('product-form') : null;
        }

        onCheckboxChange() {
          this.clearError();
          this.syncPropInputs();
          this.syncDynamicCheckout();
        }

        syncPropInputs() {
          const checked = !!(this.checkbox && this.checkbox.checked && !this.checkbox.disabled);
          this.propInputs.forEach((input) => {
            input.disabled = !checked;
          });
        }

        syncDynamicCheckout() {
          const productForm = this.productForm;
          if (!productForm) return;

          const paymentButton = productForm.querySelector('.shopify-payment-button');
          if (!paymentButton) return;

          const checked = !!(this.checkbox && this.checkbox.checked && !this.checkbox.disabled);

          if (checked) {
            if (!this.dynamicCheckoutState) {
              // Snapshot the ORIGINAL state (e.g. Shopify may already have
              // disabled this button because the variant is sold out) before
              // we touch anything, so unchecking can restore it exactly
              // instead of blindly force-enabling it.
              const controls = Array.from(paymentButton.querySelectorAll('button, input, [tabindex]'));
              this.dynamicCheckoutState = {
                ariaHidden: paymentButton.getAttribute('aria-hidden'),
                wasVisuallyHidden: paymentButton.classList.contains('visually-hidden'),
                controls: controls.map((el) => ({
                  el: el,
                  disabled: 'disabled' in el ? el.disabled : null,
                  tabindex: el.getAttribute('tabindex'),
                })),
              };
            }

            paymentButton.setAttribute('aria-hidden', 'true');
            paymentButton.classList.add('visually-hidden');
            this.dynamicCheckoutState.controls.forEach((entry) => {
              entry.el.setAttribute('tabindex', '-1');
              if ('disabled' in entry.el) entry.el.disabled = true;
            });

            if (!this.dynamicCheckoutNotice) {
              this.dynamicCheckoutNotice = document.createElement('p');
              this.dynamicCheckoutNotice.className = 'photoil-revision-option__dynamic-checkout-notice';
              this.dynamicCheckoutNotice.textContent =
                this.dataset.dynamicCheckoutNotice ||
                'このオプションをご利用の場合は「カートに追加する」よりお進みください。';
              paymentButton.insertAdjacentElement('afterend', this.dynamicCheckoutNotice);
            }
          } else {
            const state = this.dynamicCheckoutState;
            // If we never entered the "checked" branch (e.g. the box loads
            // unchecked and stays unchecked), there is nothing to restore -
            // leave the payment button exactly as Shopify/other scripts left
            // it instead of forcing aria-hidden/visually-hidden off.
            if (!state) return;

            if (state.ariaHidden === null) {
              paymentButton.removeAttribute('aria-hidden');
            } else {
              paymentButton.setAttribute('aria-hidden', state.ariaHidden);
            }
            paymentButton.classList.toggle('visually-hidden', state.wasVisuallyHidden);
            state.controls.forEach((entry) => {
              if (entry.tabindex === null) {
                entry.el.removeAttribute('tabindex');
              } else {
                entry.el.setAttribute('tabindex', entry.tabindex);
              }
              if ('disabled' in entry.el && entry.disabled !== null) entry.el.disabled = entry.disabled;
            });
            this.dynamicCheckoutState = null;

            if (this.dynamicCheckoutNotice) {
              this.dynamicCheckoutNotice.remove();
              this.dynamicCheckoutNotice = null;
            }
          }
        }

        isActive() {
          return !!(this.checkbox && this.checkbox.checked && !this.checkbox.disabled);
        }

        newReference() {
          const value = generateReference();
          if (this.referenceInput) this.referenceInput.value = value;
          return value;
        }

        showError(message) {
          if (!this.errorEl) return;
          this.errorEl.hidden = false;
          this.errorEl.textContent = message;
          this.errorEl.focus();
        }

        clearError() {
          if (!this.errorEl) return;
          this.errorEl.hidden = true;
          this.errorEl.textContent = '';
        }

        reset() {
          if (this.checkbox) {
            this.checkbox.checked = false;
            this.onCheckboxChange();
          }
        }
      }
    );
  }

  /* ---------------------------------------------------------------------
   * Submit interception: only takes over when the add-on is checked.
   * Uses a capture-phase listener on `document`, which - per the DOM
   * event dispatch order - always runs before the target-phase submit
   * listener that product-form.js attaches directly to the <form>,
   * regardless of script load order. When unchecked, this is a no-op and
   * the native product-form.js flow runs exactly as before.
   * ------------------------------------------------------------------- */

  if (!window.__photoilRevisionOptionSubmitHookInstalled) {
    window.__photoilRevisionOptionSubmitHookInstalled = true;

    document.addEventListener(
      'submit',
      function (evt) {
        const form = evt.target;
        if (!form || form.tagName !== 'FORM') return;

        // NOTE: every Shopify product form contains a hidden
        // `<input name="id">` (the selected variant id). Because of a
        // long-standing browser quirk, a named form control called "id"
        // shadows the form element's own `.id` IDL property - so
        // `form.id` can silently return that <input> element instead of
        // the form's id STRING. Always use getAttribute('id') here, which
        // is unaffected by this shadowing.
        const formId = form.getAttribute('id');
        if (!formId) return;

        const card = document.querySelector(
          'photoil-revision-option-card[data-product-form-id="' + formId + '"]'
        );
        if (!card || !card.isActive()) return;

        const productForm = form.closest('product-form');
        const submitButton = productForm
          ? productForm.querySelector('[type="submit"]')
          : form.querySelector('[type="submit"]');

        // Always take over the event for this form once checked (so the
        // native product-form.js handler never double-runs), but ignore a
        // second submit (double-click / Enter key repeat) while the first
        // one is still in flight - matching product-form.js's own guard.
        evt.preventDefault();
        evt.stopImmediatePropagation();

        if (submitButton && submitButton.getAttribute('aria-disabled') === 'true') return;

        handleCheckedSubmit(form, card, submitButton);
      },
      true
    );
  }

  function handleCheckedSubmit(form, card, submitButton) {
    const productForm = form.closest('product-form');
    const spinner = submitButton ? submitButton.querySelector('.loading__spinner') : null;
    const errorMessageWrapper = productForm
      ? productForm.querySelector('.product-form__error-message-wrapper')
      : null;
    const errorMessageEl = errorMessageWrapper
      ? errorMessageWrapper.querySelector('.product-form__error-message')
      : null;

    function setFormError(message) {
      if (!errorMessageWrapper || !errorMessageEl) return;
      errorMessageWrapper.hidden = !message;
      if (message) errorMessageEl.textContent = message;
    }

    function finalizeButton() {
      if (submitButton) {
        submitButton.classList.remove('loading');
        submitButton.removeAttribute('aria-disabled');
      }
      if (spinner) spinner.classList.add('hidden');
    }

    card.clearError();
    setFormError(false);

    if (submitButton) {
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('loading');
    }
    if (spinner) spinner.classList.remove('hidden');

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const referenceId = card.newReference();
    const addonVariantId = card.dataset.addonVariantId;

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(form);
    if (cart) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map(function (section) {
          return section.id;
        })
      );
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }
    config.body = formData;

    const mainQuantity = Number(formData.get('quantity')) || 1;
    const mainVariantId = formData.get('id');

    fetch(window.routes.cart_add_url, config)
      .then(function (response) {
        return response.json();
      })
      .then(function (mainResponse) {
        if (mainResponse.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'photoil-revision-option',
            productVariantId: mainVariantId,
            errors: mainResponse.errors || mainResponse.description,
            message: mainResponse.message,
          });
          setFormError(mainResponse.description || mainResponse.message);
          finalizeButton();
          return;
        }

        addAddon(mainResponse);
      })
      .catch(function () {
        card.showError(
          card.dataset.errorGeneric ||
            '通信エラーが発生しました。通信環境をご確認のうえ、再度お試しください。本体商品もまだカートに追加されていません。'
        );
        finalizeButton();
      });

    function addAddon(mainResponse) {
      const addonBody = {
        id: addonVariantId,
        quantity: mainQuantity,
        properties: {
          _revision_addon: 'true',
          _parent_product_id: String(mainResponse.product_id || ''),
          _parent_variant_id: String(mainResponse.variant_id || mainVariantId || ''),
          _parent_product_title: mainResponse.product_title || '',
          _photoil_item_reference: referenceId,
        },
      };

      if (cart) {
        addonBody.sections = cart.getSectionsToRender().map(function (section) {
          return section.id;
        });
        addonBody.sections_url = window.location.pathname;
      }

      const addonConfig = fetchConfig('json');
      addonConfig.body = JSON.stringify(addonBody);

      fetch(window.routes.cart_add_url, addonConfig)
        .then(function (response) {
          return response.json();
        })
        .then(function (addonResponse) {
          if (addonResponse.status) {
            handleAddonFailure(mainResponse, addonResponse.description || addonResponse.message);
            return;
          }
          finishSuccess(mainResponse, addonResponse);
        })
        .catch(function () {
          handleAddonFailure(mainResponse, null);
        });
    }

    function handleAddonFailure(mainResponse) {
      const message =
        card.dataset.errorAddonFailed ||
        'オプション商品の追加に失敗しました。チェックを外して再度お試しいただくか、カスタマーサポートへご連絡ください。';

      // Best-effort rollback: remove the main line we just added, keyed by
      // its unique cart line `key`, so the cart does not silently keep a
      // "完成前確認・1回修正オプション：あり" line with no matching add-on line.
      fetch(window.routes.cart_change_url, {
        ...fetchConfig('json'),
        body: JSON.stringify({ id: mainResponse.key, quantity: 0 }),
      })
        .then(function (response) {
          return response.json();
        })
        .then(function () {
          card.showError(message + '（本体商品の追加も取り消しました）');
        })
        .catch(function () {
          card.showError(
            message +
              '（本体商品のみカートに残っている可能性があります。お手数ですがカート内容をご確認ください）'
          );
        })
        .finally(function () {
          finalizeButton();
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'photoil-revision-option', cartData: mainResponse });
        });
    }

    function finishSuccess(mainResponse, addonResponse) {
      if (cart) {
        if (cart.tagName === 'CART-NOTIFICATION') {
          cart.renderContents(Object.assign({}, addonResponse, { key: mainResponse.key }));
        } else {
          cart.renderContents(addonResponse);
        }
        card.reset();
        finalizeButton();
      } else {
        window.location = window.routes.cart_url;
      }
    }
  }

  /* ---------------------------------------------------------------------
   * Cart page / cart drawer: lock quantity + provide a combined "remove
   * set" action for the main <-> add-on line pair. Implemented as a real
   * custom element so it re-initialises automatically whenever the cart
   * markup is replaced (drawer re-render, theme editor, etc.) - no manual
   * re-binding logic required.
   * ------------------------------------------------------------------- */

  if (!customElements.get('photoil-linked-cart-item')) {
    customElements.define(
      'photoil-linked-cart-item',
      class PhotoilLinkedCartItem extends HTMLElement {
        connectedCallback() {
          // connectedCallback (rather than the constructor) so this reliably
          // re-runs every time the cart markup is replaced (drawer/cart page
          // re-render), including the rare case where an element instance is
          // disconnected and reconnected without being recreated.
          if (this.photoilBound) return;
          this.photoilBound = true;

          const row = this.closest('.cart-item');
          if (!row) return;

          this.lockQuantityControls(row);

          const button = this.querySelector('[data-photoil-remove-set]');
          if (button) button.addEventListener('click', this.onRemoveSet.bind(this));
        }

        lockQuantityControls(row) {
          row.querySelectorAll('.quantity__input').forEach(function (input) {
            input.readOnly = true;
            input.setAttribute('aria-readonly', 'true');
          });
          row.querySelectorAll('.quantity__button').forEach(function (button) {
            button.disabled = true;
          });

          const removeButton = row.querySelector('cart-remove-button');
          if (removeButton) removeButton.hidden = true;
        }

        onRemoveSet(evt) {
          evt.preventDefault();
          const button = evt.currentTarget;
          if (button.disabled) return;
          button.disabled = true;

          const reference = this.dataset.photoilReference;
          const ownKey = this.dataset.cartKey;

          const rows = Array.from(document.querySelectorAll('[data-photoil-reference]')).filter(function (el) {
            return el.dataset.photoilReference === reference;
          });

          const keys = Array.from(
            new Set(
              rows
                .map(function (el) {
                  return el.dataset.cartKey;
                })
                .concat([ownKey])
                .filter(Boolean)
            )
          );

          const updates = {};
          keys.forEach(function (key) {
            updates[key] = 0;
          });

          // Reuse Dawn's own cart-items/cart-drawer-items component (already
          // present in the DOM) for the section list and HTML-extraction
          // helper, so the order total, live region and header cart bubble
          // are refreshed exactly like a normal quantity change - not just
          // the product rows.
          const cartItemsEl = this.closest('cart-items') || this.closest('cart-drawer-items');
          const sections = cartItemsEl ? cartItemsEl.getSectionsToRender() : [];

          const body = { updates: updates };
          if (cartItemsEl && sections.length) {
            body.sections = sections.map(function (section) {
              return section.section;
            });
            body.sections_url = window.location.pathname;
          }

          fetch(window.routes.cart_update_url, {
            ...fetchConfig('json'),
            body: JSON.stringify(body),
          })
            .then(function (response) {
              return response.json();
            })
            .then(function (state) {
              if (state.errors) {
                window.alert('削除に失敗しました。ページを再読み込みしてから、もう一度お試しください。');
                button.disabled = false;
                return;
              }

              if (cartItemsEl && sections.length && state.sections) {
                sections.forEach(function (section) {
                  const container = document.getElementById(section.id);
                  if (!container) return;
                  // Match Dawn's own fallback exactly (assets/cart.js
                  // updateQuantity): some sections (e.g. cart-icon-bubble)
                  // have no `.shopify-section` descendant of their own, so
                  // querySelector returns null and the container itself is
                  // the element to replace into.
                  const target = section.selector
                    ? container.querySelector(section.selector) || container
                    : container;
                  target.innerHTML = cartItemsEl.getSectionInnerHTML(state.sections[section.section], section.selector);
                });

                cartItemsEl.classList.toggle('is-empty', state.item_count === 0);
                const cartFooter = document.getElementById('main-cart-footer');
                if (cartFooter) cartFooter.classList.toggle('is-empty', state.item_count === 0);
                const cartDrawerWrapper = document.querySelector('cart-drawer');
                if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', state.item_count === 0);
              } else {
                // Fallback: no cart-items/cart-drawer-items ancestor found -
                // publish so any generic cart-update listener can refresh.
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'photoil-linked-cart-item', cartData: state });
              }
            })
            .catch(function () {
              window.alert('通信エラーが発生しました。ページを再読み込みしてから、もう一度お試しください。');
              button.disabled = false;
            });
        }
      }
    );
  }
})();
